import { useCallback, useEffect, useState } from 'react';
import type { SkillStudioEvalGroundTruthSubmitPayload } from 'asper_design_library';
import type {
    EvalGroundTruthRequest,
    EvalGroundTruthUpdateRequest,
    QuestionSetInfo,
} from '../types';
import { createGroundTruth, getGroundTruthSets, updateGroundTruth } from '../../services/evalService';
import { listContent, listTenants } from '../../services/promptService';
import type { FileEntry, Tenant } from '../../prompts/types';

export function useEvalGroundTruthForm(open: boolean) {
    const [questionSets, setQuestionSets] = useState<QuestionSetInfo[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [taskFileEntries, setTaskFileEntries] = useState<FileEntry[]>([]);

    useEffect(() => {
        if (!open) return;
        getGroundTruthSets().then(setQuestionSets).catch(() => {});
        listTenants().then((response) => setTenants(response.tenants)).catch(() => {});
    }, [open]);

    const onTenantChange = useCallback((tenant: string) => {
        if (!tenant) {
            setTaskFileEntries([]);
            return;
        }
        listContent(tenant)
            .then((tree) => setTaskFileEntries(tree.tasks ?? []))
            .catch(() => setTaskFileEntries([]));
    }, []);

    const onSubmit = useCallback(async (payload: SkillStudioEvalGroundTruthSubmitPayload) => {
        if (payload.mode === 'edit' && payload.entryId) {
            return updateGroundTruth(
                payload.entryId,
                payload.body as EvalGroundTruthUpdateRequest,
            );
        }
        const body = payload.body as EvalGroundTruthRequest;
        return createGroundTruth({
            question_set: body.question_set,
            question: body.question,
            target_agent: body.target_agent,
            ground_truth: body.ground_truth,
            fixture: body.fixture,
            status: body.status,
            metadata: payload.metadata,
            initial_comment: body.initial_comment,
        });
    }, []);

    return {
        questionSets,
        tenants,
        taskFileEntries,
        onTenantChange,
        onSubmit,
    };
}
